/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { type QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Card,
  CardContent,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { FeatureFlag } from '@repo/shared-common'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import ChatGroupMemberLimitNotice from '@web/src/features/chat/chat-group-member-limit-notice/chat-group-member-limit-notice'
import ChatMembersHeader from '@web/src/features/chat/chat-members-header/chat-members-header'
import { ADD_CHAT_MEMBERS } from '@web/src/features/chat/chat-queries'
import { isChatGroupAtMemberLimit } from '@web/src/features/chat/chat-utils'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import i18n from '@web/src/lib/intl/i18n'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { UserPlusIcon, UserSearchIcon, UsersIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  ChatInviteSortEnum,
  ChatMembersInvitePageQuery,
  ChatMembersInvitePageQueryVariables,
  ChatMembersPageRoomQuery,
} from 'types/graphql'
import { z } from 'zod'
import { CHAT_MEMBERS_PAGE_QUERY, CHAT_MEMBERS_PAGE_ROOM_QUERY } from './$roomId_.members'

export const CHAT_INVITE_SEARCH_QUERY: TypedDocumentNode<
  ChatMembersInvitePageQuery,
  ChatMembersInvitePageQueryVariables
> = gql`
  query ChatMembersInvitePageQuery($filter: ChatInviteSearchFilter!, $page: Int, $sortBy: ChatInviteSortEnum) {
    chatInviteSearchPage(filter: $filter, page: $page, sortBy: $sortBy) {
      entries {
        profile {
          ...ProfileBasicInfo
        }
        isMember
      }
      count
      perPage
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const CHAT_INVITE_PAGE_SIZE = 50

type ChatInviteSearchInput = {
  page?: number
  name?: string
  sortBy?: string
}

type ChatInviteLoaderDeps = {
  page: number
  name?: string
  sortBy: string
}

function resolveChatInviteSearchParams(search: ChatInviteSearchInput): ChatInviteLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    sortBy: search.sortBy ?? 'NAME',
  }
}

function chatInviteQueryVariables(roomId: string, deps: ChatInviteLoaderDeps): ChatMembersInvitePageQueryVariables {
  return {
    page: deps.page,
    sortBy: deps.sortBy as ChatInviteSortEnum,
    filter: {
      chatRoomId: roomId,
      name: deps.name,
      excludeSelf: false,
      maxResults: CHAT_INVITE_PAGE_SIZE,
    },
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/chat/$roomId_/invite')({
  component: ChatMembersInvitePage,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
        name: '',
        sortBy: 'NAME',
      }),
    ],
  },
  head: () => {
    const t = i18n.t.bind(i18n)
    return seo({
      title: `${t('Invite Members')} | ${env.VITE_APP_NAME}`,
      description: t('Invite people to a conversation.'),
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    })
  },
  loaderDeps: ({ search }) => resolveChatInviteSearchParams(search),
  loader: async ({ context, deps, params }) => {
    const resolvedDeps = deps as ChatInviteLoaderDeps
    const variables = chatInviteQueryVariables(params.roomId, resolvedDeps)

    const [{ queryRef: roomQueryRef }, { queryRef: inviteQueryRef }] = await Promise.all([
      queryAndPreload(context, CHAT_MEMBERS_PAGE_ROOM_QUERY, {
        variables: { id: params.roomId },
      }),
      queryAndPreload(context, CHAT_INVITE_SEARCH_QUERY, { variables }),
    ])

    return { roomQueryRef, inviteQueryRef }
  },
})

function ChatMembersInvitePage() {
  const { t } = useTranslation()
  const { getFeature } = useAuth()
  const maxMembers = getFeature(FeatureFlag.CHAT_GROUP_MAX_MEMBERS)
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveChatInviteSearchParams(search), [search])
  const client = useApolloClient()
  const navigate = useNavigate()
  const formRef = useRef(null)

  const { roomQueryRef, inviteQueryRef } = Route.useLoaderData()
  const { data: roomData, error: roomError } = useReadQuery(roomQueryRef as QueryRef<ChatMembersPageRoomQuery>)
  const { data, error } = useReadQuery(inviteQueryRef as QueryRef<ChatMembersInvitePageQuery>)
  const [isLoading, setIsLoading] = useState(false)
  const [invitingProfileId, setInvitingProfileId] = useState<string | null>(null)

  const routerIsPending = useRouterState({ select: (s) => s.status === 'pending' })

  useEffect(() => {
    setIsLoading(routerIsPending)
  }, [routerIsPending])

  const formMethods = useForm({
    defaultValues: {
      page: resolvedSearch.page,
      name: resolvedSearch.name ?? '',
      sortBy: resolvedSearch.sortBy,
    },
  })

  const [addMembers] = useMutation(ADD_CHAT_MEMBERS, {
    onCompleted: async () => {
      await client.refetchQueries({
        include: [CHAT_INVITE_SEARCH_QUERY, CHAT_MEMBERS_PAGE_QUERY, CHAT_MEMBERS_PAGE_ROOM_QUERY],
      })
      setInvitingProfileId(null)
    },
    onError: (mutationError) => {
      toast.error(t('Error sending invitation: {{message}}', { message: mutationError.message }))
      setInvitingProfileId(null)
    },
  })

  const filterResults = useCallback(() => {
    const fieldData = formMethods.getValues()
    navigate({
      search: {
        page: fieldData.page,
        name: fieldData.name || undefined,
        sortBy: fieldData.sortBy,
      },
      replace: true,
      viewTransition: false,
    } as never)
  }, [formMethods, navigate])

  const handleLiveFilter = useCallback(() => {
    filterResults()
  }, [filterResults])

  const handleResetFilters = () => {
    formMethods.reset({
      page: 1,
      name: '',
      sortBy: 'NAME',
    })

    navigate({
      search: {
        page: 1,
        name: undefined,
        sortBy: 'NAME',
      },
      replace: true,
      viewTransition: false,
    } as never)
  }

  const handlePageChange = useCallback(
    (nextPage: { page: number }) => {
      navigate({
        search: {
          ...search,
          page: nextPage.page,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: {
          ...search,
          page: 1,
          name: value || undefined,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const room = roomData?.chatRoom
  const atMemberLimit = isChatGroupAtMemberLimit(room, maxMembers)

  const handleInvite = (profileId: string) => {
    if (!room || room.archivedAt || !room.canInvite || atMemberLimit) {
      if (atMemberLimit) {
        toast.error(
          t('This group has reached the {{count}} member limit for your membership level.', { count: maxMembers })
        )
      }
      return
    }
    setInvitingProfileId(profileId)
    addMembers({
      variables: {
        chatRoomId: room.id,
        profileIds: [profileId],
      },
    })
  }

  const pageData = useMemo(
    () => data?.chatInviteSearchPage || { entries: [], count: 0, perPage: 50 },
    [data?.chatInviteSearchPage]
  )
  const currentPage = resolvedSearch.page

  const getStatusDisplay = (
    entry: NonNullable<ChatMembersInvitePageQuery['chatInviteSearchPage']>['entries'][number]
  ) => {
    if (entry.isMember) {
      return (
        <div className="flex max-w-full flex-nowrap items-center gap-1.5">
          <UsersIcon className="size-4 shrink-0" />
          <span className="whitespace-nowrap">{t('Member')}</span>
        </div>
      )
    }

    const inviting = invitingProfileId === entry.profile.id

    return (
      <Button
        disabled={invitingProfileId != null || atMemberLimit}
        onClick={() => handleInvite(entry.profile.id)}
        size="sm"
        type="button"
      >
        {inviting ? <Spinner className="size-4" /> : <UserPlusIcon className="size-4" />}
        {t('Invite')}
      </Button>
    )
  }

  if (error || roomError) {
    return <ErrorAlert error={error || roomError} />
  }

  if (!room) {
    return (
      <div className="mx-auto w-full max-w-4xl p-2">
        <p className="py-16 text-center text-muted-foreground">{t('This conversation is not available.')}</p>
      </div>
    )
  }

  if (room.archivedAt || !room.canInvite) {
    return (
      <div className="mx-auto w-full max-w-4xl p-2">
        <div className="flex w-full flex-col items-stretch gap-4">
          <ChatMembersHeader activeTab="invite" room={room} />
          <p className="py-16 text-center text-muted-foreground">
            {room.archivedAt
              ? t('This conversation is archived and read-only.')
              : t('You do not have permission to invite people to this conversation.')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <div className="flex w-full flex-col items-stretch gap-4">
        <ChatMembersHeader activeTab="invite" room={room} />
        <ChatGroupMemberLimitNotice room={room} />

        <Form className="w-full" formMethods={formMethods} ref={formRef}>
          <div className="flex w-full flex-col items-stretch gap-4">
            <SearchExpand
              clearSearchLabel={t('Clear search')}
              inputProps={{ 'aria-label': t('Search profiles') }}
              loading={isLoading}
              minChars={0}
              name="name"
              onSearchChange={handleSearchChange}
              placeholder={t('Search profiles by name...')}
              toggleFiltersLabel={t('Toggle filters')}
            >
              <Card className="gap-0 py-0">
                <CardContent className="px-3 py-3">
                  <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-wrap items-start gap-4">
                      <NativeSelectFormField label={t('Sort By')} name="sortBy" onValueChange={handleLiveFilter}>
                        <option value="NEWEST">{t('Newest')}</option>
                        <option value="OLDEST">{t('Oldest')}</option>
                        <option value="NAME">{t('Name')}</option>
                      </NativeSelectFormField>
                    </div>

                    <div className="flex w-full justify-end">
                      <Button onClick={handleResetFilters} type="button" variant="secondary">
                        {t('Reset')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SearchExpand>

            {pageData.entries.length > 0 ? (
              <div className="flex w-full flex-col gap-2">
                {pageData.entries.map((entry) => (
                  <Card
                    className={cn(
                      'w-full border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1',
                      entry.isMember && 'opacity-40'
                    )}
                    key={entry.profile.id}
                    size="sm"
                  >
                    <CardContent className="px-2 py-1">
                      <div className="flex items-center justify-between gap-2">
                        <SFProfileBanner
                          className="cursor-pointer"
                          onClick={() =>
                            navigate({
                              to: '/profile/$profileId/about',
                              params: { profileId: entry.profile.id },
                            } as never)
                          }
                          profile={entry.profile}
                          size="xl"
                        />

                        {getStatusDisplay(entry)}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <PaginationControl
                  count={pageData.count}
                  onPageChange={handlePageChange}
                  page={currentPage}
                  pageSize={pageData.perPage}
                />
              </div>
            ) : null}

            {pageData.entries.length === 0 ? (
              <Empty className="py-20">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UserSearchIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t('No profiles found')}</EmptyTitle>
                  {resolvedSearch.name ? (
                    <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
                  ) : null}
                </EmptyHeader>
              </Empty>
            ) : null}
          </div>
        </Form>
      </div>
    </div>
  )
}
